import { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { BottomNav } from "@/components/app/bottom-nav";
import { PageLoading } from "@/components/app/states";
import { useLocaleSwitch } from "@/i18n";
import { ApiError } from "@/lib/api";
import { useMe } from "@/lib/queries";

/** Renders children only for signed-in users, otherwise redirects to /login. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const me = useMe();
  const location = useLocation();
  const { adoptAccountLocale } = useLocaleSwitch();
  const accountLocale = me.data?.profile.locale;
  useEffect(() => {
    if (accountLocale) adoptAccountLocale(accountLocale);
  }, [accountLocale, adoptAccountLocale]);
  if (me.isPending) return <PageLoading />;
  if (me.error instanceof ApiError && me.error.status === 401) {
    // Back to this page after signing in (e.g. a plan someone shared).
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (me.isError) throw me.error;
  return children;
}

/**
 * App shell: the document itself never scrolls, only the content area does,
 * and the navigation is the last row of a full-height column. On phones this
 * keeps the browser toolbar from collapsing/expanding between pages of
 * different length, which made a `position: fixed` bar jump up and down.
 *
 * It is pinned to the viewport edges (`fixed inset-0`); for the short iOS
 * home-screen viewport see `.ios-standalone .pb-safe` in index.css.
 */
export function AppLayout() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  return (
    <RequireAuth>
      <div className="app-shell fixed inset-0 flex flex-col overflow-hidden">
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
          <Outlet />
        </div>
        <BottomNav />
      </div>
    </RequireAuth>
  );
}

/** Full-screen pages without the bottom navigation (guided workout). */
export function AuthOnlyLayout() {
  return (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  );
}

/** Where to go after signing in: the page that asked for it (see RequireAuth) or the dashboard. */
export function afterSignIn(state: unknown) {
  const from = (state as { from?: string } | null)?.from;
  return from?.startsWith("/") && !from.startsWith("//") ? from : "/dashboard";
}

/** Landing/login pages: signed-in users go straight on (this also runs right after signing in). */
export function PublicOnly() {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <PageLoading />;
  if (me.isSuccess) return <Navigate to={afterSignIn(location.state)} replace />;
  return <Outlet />;
}
